import type { CountryCode } from 'libphonenumber-js/types'
import { Box, Button, Center, Text } from 'native-base'
import qs from 'qs'
import React, { PropsWithChildren, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import useSWRImmutable from 'swr/immutable'

import { ErrorImage } from '../components/images/Error'
import { ShowEnvironmentVariables } from '../components/ShowEnvironmentVariables'
import { ENVIRONMENT } from '../constants/config'
import { useAppStatus } from '../hooks/use-app-status'
import fetcher from '../lib/fetcher'
import { SupportedLanguageKeys } from '../services/i18n/types'
import { isNative } from '../util/platform'
import { restartApp } from '../util/restart'
import { makeStrapiImageUrl } from '../util/url'

export enum PrescriptionProvider {
  MEMED = 'memed',
  MEDIKIT = 'medikit',
}

export enum InvoicingProviders {
  OMIE = 'omie',
  DUMMY = 'dummy',
}

export enum SupportProvider {
  Zendesk = 'zendesk',
  Custom = 'custom',
}

export enum ContactMethod {
  PHONE = 'phone',
  VIDEO = 'video',
}

export enum ConfigCountryCode {
  ES = 'ES',
}

export enum CurrencyCode {
  USD = 'USD',
  EUR = 'EUR',
}

export type Config = {
  allowedLinkDomains: Array<string>
  allowedUploadFileTypes: Array<string>
  completionTime: number
  countryCodes: Array<CountryCode>
  emergencyPhoneNumber: string
  summaryRetryLifespan?: number
  enableChatbotSummary?: boolean
  enablePromoCode?: boolean
  enableCookieConsent?: boolean
  enableMfa: boolean
  maintenanceMode: boolean
  marketingHomepageURL: string
  landingPageURL: string
  maxUploadFileSize: number
  otpLength: number
  otpValidity: number
  partnerLogo: string
  partnerName: string
  timezone: string
  patientSessionLengthInMins: number
  patientAppLanguage: SupportedLanguageKeys
  warningBeforeClosingInMins: number
  warningBeforePatientLogoutInMins: number
  clinicianSlotLockMinutes: number
  consultationCountdownMinutes: number
  partnerConfig: PartnerConfig
  consultationBookingMaxDaysInFuture: number
  schedulingAvailabilityPageSizeDays: number
  availableContactMethods: ContactMethod[]
  preferredContactMethod: ContactMethod
  minCancellationTimeInAdvanceMinutes: number
  canPatientAddDependents?: boolean
  maxAgeAllowedForDependants: number
  minAgeRequiredToAddDependants: number
  socialProof?: {
    text: string
    rating: number
    reviews: number
  }
  migrationDate?: string
  minimumSupportedAppVersion: {
    ios?: string
  }
}

export type PartnerConfig = {
  socialLoginEnabled: boolean
  cpfValidationEnabled: boolean
  billingAddressEnabled: boolean
  installments: number
  isMemed: boolean
  isMedikit: boolean
  invoicing: {
    enabled: boolean
    provider?: InvoicingProviders
    days?: {
      pix: number
      creditCard: number
    }
  }
  countryCode: ConfigCountryCode
  currencyCode: CurrencyCode
  familyProfilesEnabled: boolean
  prescriptionProvider: PrescriptionProvider
  supportProvider: {
    type: SupportProvider
    info?: {
      email: string
      whatsapp: string
    }
  }
  dateFormats: {
    timeShort: string // e.g. 14:30
    date: string // e.g. 12/12/2020
    dateShort: string // e.g. 12/12/20
    dateLongMonth: string // e.g. Saturday 12th December
    dateShortDayLongMonth: string // e.g. Sat 12th December
    dateTimeLongMonth: string // e.g. 12 December 2020, 18:45
  }
  showRegistrationOnboarding?: boolean
  /** For example, "itms-apps://itunes.apple.com/app/id1074412639" */
  appStoreItmsAppsUrl?: string
}

export type ConfigContextType = {
  config: Config
  updateConfig: (config: { [K in keyof Config]?: Config[K] }) => void
}

export const ConfigContext = React.createContext<ConfigContextType | undefined>(undefined)

export const ConfigProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const { t } = useTranslation()
  const { setAppIsReady } = useAppStatus()
  const query = qs.stringify({ populate: { partnerLogo: '*', socialProof: '*' } })
  const [configState, setConfigState] = useState<Config>()
  const { data, error } = useSWRImmutable(`/api/backend-config?${query}`, {
    fetcher: fetcher.strapi,
  })

  // On initial load, check if user is logged in and load the profile
  useEffect(() => {
    async function initialise() {
      if (!data?.data?.attributes) return

      setConfigState({
        ...data.data.attributes,
        partnerConfig: {
          ...data.data.attributes.partnerConfig,
          isMemed: data.data.attributes.partnerConfig?.prescriptionProvider === PrescriptionProvider.MEMED,
          isMedikit: data.data.attributes.partnerConfig?.prescriptionProvider === PrescriptionProvider.MEDIKIT,
        },
        partnerLogo: data.data.attributes.partnerLogo?.data?.attributes.url
          ? makeStrapiImageUrl(data.data.attributes.partnerLogo?.data?.attributes.url)
          : null,
        uiConfig: {
          showRegistrationOnboarding: !!data.data.attributes.partnerConfig?.showRegistrationOnboarding,
        },
      })

      // TODO: We can improve this by using a better way to check if the app is ready, like UI loaded
      setAppIsReady(true)
    }
    initialise()
  }, [data?.data?.attributes, setAppIsReady])

  const updateConfig = useCallback((config: { [K in keyof Config]?: Config[K] }) => {
    return setConfigState(c => {
      if (!c) return
      return { ...c, ...config }
    })
  }, [])

  const context = useMemo(() => ({ config: configState, updateConfig }), [configState, updateConfig])

  if (error) {
    setAppIsReady(true)

    if (isNative() && !ENVIRONMENT.includes('prod')) {
      console.error('ConfigProvider error', error)
      return (
        <Box p={4}>
          <Text>Error loading config from Strapi</Text>
          <Text>{JSON.stringify(error, null, 2)}</Text>
          <Text>error.message: {error.message}</Text>
          <ShowEnvironmentVariables />
        </Box>
      )
    }

    return (
      <Center alignItems="center" minHeight="full" p={4}>
        <ErrorImage size={160} />
        <Text fontSize="xl" bold mt={7}>
          {t('errorBoundary.heading')}
        </Text>
        <Text fontSize="md" mt={4}>
          {t('errorBoundary.text')}
        </Text>
        <Button size="sm" width={'full'} mt={5} p={3} onPress={restartApp}>
          {t('errorBoundary.button')}
        </Button>
      </Center>
    )
  }

  if (!configState) {
    return null
  }

  return <ConfigContext.Provider value={context as ConfigContextType}>{children}</ConfigContext.Provider>
}

export const useConfig = () => {
  const context = useContext(ConfigContext)

  if (!context) {
    throw new Error('useConfig() must be wrapped inside ConfigProvider.')
  }

  return context
}
